import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Calendar, Users, DollarSign, Gift, CheckCircle2, X, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

interface GiftGroupAcceptanceProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; memberId?: string; tab?: string }) => void;
  memberId: string | null;
}

interface GiftGroupInvitation {
  member_id: string;
  member_contact: string; // The invitee's email or phone
  gift_group_id: string;
  gift_group_title: string;
  gift_group_type_id: number;
  gift_group_type_name: string;
  gift_group_type_description: string;
  gift_date: string;
  recommended_contribution: number | null;
  total_members: number;
  accepted_members: number;
  invited_by_name?: string;
  invited_by_email?: string;
  invited_by_phone?: string;
}

export function GiftGroupAcceptance({ onNavigate, memberId }: GiftGroupAcceptanceProps) {
  const [invitation, setInvitation] = useState<GiftGroupInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeclined, setIsDeclined] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);


  useEffect(() => {
    if (!memberId) {
      setError('No invitation selected');
      setLoading(false);
      return;
    }

    const fetchInvitation = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getGiftGroupInvitation(memberId);
        setInvitation(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load invitation';
        setError(errorMessage);
        toast.error('Failed to load invitation', {
          description: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [memberId]);

  // Clear the "just authenticated" flag if it exists (user authenticated and returned to this page)
  useEffect(() => {
    if (sessionStorage.getItem('just_authenticated') === 'true') {
      sessionStorage.removeItem('just_authenticated');
      // Show welcome message but don't auto-accept
      toast.success('Welcome! Please review the invitation below.', {
        description: 'Click "Join Gift Group" to accept the invitation.',
      });
    }
  }, []);

  const handleJoin = async () => {
    if (!memberId || !invitation) return;

    // Check if user is authenticated
    if (!api.isAuthenticated()) {
      // User needs to sign in first - redirect to login
      toast.info('Please sign in to join this gift group', {
        description: 'You\'ll be able to accept the invitation after signing in.',
        duration: 5000,
      });
      // Store the invitation URL so we can redirect back after login
      sessionStorage.setItem('auth_redirect', `/invitation/${memberId}`);
      // Navigate to login screen
      onNavigate('login');
      return;
    }

    // User is authenticated - accept invitation
    try {
      setSubmitting(true);
      await api.acceptGiftGroupInvitation(memberId);
      toast.success('Welcome to the gift group!', {
        description: 'Check your dashboard to contribute to the gift pool.',
      });
      // Redirect to dashboard
      onNavigate('dashboard', { tab: 'todo' });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to accept invitation';
      toast.error('Failed to join gift group', {
        description: errorMessage,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!memberId || !invitation) return;

    // Must be authenticated to decline
    if (!api.isAuthenticated()) {
      toast.info('Please sign in first', {
        description: 'Authentication is required to decline invitations.',
      });
      return;
    }

    // Decline invitation
    try {
      setSubmitting(true);
      await api.declineGiftGroupInvitation(memberId, declineReason);
      toast.success('Invitation declined', {
        description: 'The gift group creator has been notified.',
      });
      setIsDeclined(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to decline invitation';
      toast.error('Failed to decline invitation', {
        description: errorMessage,
      });
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    let date: Date;
    
    // If it's an ISO timestamp (contains 'T'), parse it normally
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      // Parse YYYY-MM-DD as local date, not UTC (prevents off-by-one day issues)
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day);
    }
    
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="p-8 border border-primary/20 shadow-lg">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading invitation...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="p-8 border border-primary/20 shadow-lg max-w-md">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || 'Invitation not found'}</AlertDescription>
          </Alert>
          <Button
            className="mt-4 w-full bg-primary hover:bg-primary/90"
            onClick={() => onNavigate('dashboard')}
          >
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (isDeclined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 border border-primary/20 shadow-lg text-center">
          <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
            <X className="w-8 h-8 text-muted-foreground" />
          </div>
          <h2 className="text-primary mb-2">Invitation Declined</h2>
          <p className="text-muted-foreground mb-6">The gift group creator has been notified.</p>
          <Button onClick={() => onNavigate('dashboard')} className="w-full bg-primary hover:bg-primary/90">
            Return to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-8 border border-primary/20 shadow-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Gift className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-primary mb-2">Gift Group Invitation</h1>
          <p className="text-muted-foreground">
            {invitation.invited_by_name || invitation.invited_by_email || invitation.invited_by_phone || 'Someone'} has invited you to join a gift group
          </p>
        </div>

        {/* Gift Group Details */}
        <Card className="p-6 bg-secondary/20 border border-primary/20 mb-8">
          <div className="space-y-4">
            <div>
              <h2 className="text-primary mb-1">{invitation.gift_group_title}</h2>
              <Badge variant="secondary">{invitation.gift_group_type_description}</Badge>
            </div>

            <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-primary/20">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground mt-1" />
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Gift Date</div>
                  <div>{formatDate(invitation.gift_date)}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-muted-foreground mt-1" />
                <div>
                  <div className="text-xs text-muted-foreground uppercase">Gifters</div>
                  <div>{invitation.accepted_members} of {invitation.total_members} accepted</div>
                </div>
              </div>

              {invitation.recommended_contribution && (
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-muted-foreground mt-1" />
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">Recommended Contribution</div>
                    <div>{formatCurrency(invitation.recommended_contribution)}</div>
                  </div>
                </div>
              )}

              {invitation.recommended_contribution && (
                <div className="flex items-start gap-3">
                  <Gift className="w-5 h-5 text-muted-foreground mt-1" />
                  <div>
                    <div className="text-xs text-muted-foreground uppercase">Target Gift Pool</div>
                    <div>{formatCurrency(invitation.recommended_contribution * invitation.total_members)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* What Happens Next */}
        <Card className="p-4 border border-primary/20 mb-8">
          <h3 className="text-primary mb-3">What Happens Next?</h3>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>You'll contribute to the shared gift pool</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>The gift date is added to your calendar</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>You will make gift suggestions</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>AI will suggest perfect gifts</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>You'll collaborate with other gifters to rank gift ideas</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <span>We automatically order the top-ranked gift to arrive by gift date</span>
            </li>
          </ul>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col gap-4">
          <Button
            onClick={handleJoin}
            size="lg"
            className="w-full bg-primary hover:bg-primary/90"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {api.isAuthenticated() ? 'Joining...' : 'Sending Magic Link...'}
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Join Gift Group
              </>
            )}
          </Button>

          <details className="text-center">
            <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
              Not interested? Decline invitation
            </summary>
            <div className="mt-4 p-4 border border-primary/20 rounded-lg bg-secondary/20">
              <Textarea
                placeholder="Optional: Share why you're declining..."
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="mb-3"
                rows={3}
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeclineReason('')}
                  className="w-full border-primary/30"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDecline}
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Declining...
                    </>
                  ) : (
                    'Decline Invitation'
                  )}
                </Button>
              </div>
            </div>
          </details>
        </div>
      </Card>
    </div>
  );
}